
import React from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ScrollArea } from '@/components/ui/scroll-area';
import { formatCurrency } from '@/utils/formatters';
import { RoyaltyReport } from '@/types';
import { normalizeCountryCode } from '@/utils/countries';

interface QuarterlySalesBreakdownProps {
  quarterlyReports?: RoyaltyReport[];
  fallbackReport?: RoyaltyReport;
  totalSales: number;
  totalGrossSales: number;
  totalRoyaltyAmount: number;
}

export default function QuarterlySalesBreakdown({
  quarterlyReports,
  fallbackReport,
  totalSales,
  totalGrossSales,
  totalRoyaltyAmount
}: QuarterlySalesBreakdownProps) {
  // Only show data for the current quarter/year if we have a fallback report
  const currentQuarter = fallbackReport?.quarter;
  const currentYear = fallbackReport?.year;

  // Filter quarterly reports to only include the current quarter/year
  const filteredReports = quarterlyReports?.filter(report => 
    report.quarter === currentQuarter && report.year === currentYear
  ) || [];

  // Calculate previous royalty income (using netSales field - allow 0 values)
  const calculatePreviousRoyaltyIncome = (bookReport: RoyaltyReport) => {
    return bookReport.netSales ?? 0; // Use nullish coalescing to allow 0 values
  };

  const totalPreviousRoyaltyIncome = filteredReports.length > 0 
    ? filteredReports.reduce((sum, r) => sum + calculatePreviousRoyaltyIncome(r), 0)
    : (fallbackReport ? calculatePreviousRoyaltyIncome(fallbackReport) : 0);

  return (
    <Card>
      <CardHeader>
        <CardTitle>Sales Breakdown for Q{currentQuarter} {currentYear}</CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        <ScrollArea className="w-full">
          <div className="min-w-[800px]">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="min-w-[200px]">Book Title</TableHead>
                  <TableHead className="min-w-[80px]">Format</TableHead>
                  <TableHead className="min-w-[80px]">Country</TableHead>
                  <TableHead className="min-w-[80px]">Quarter</TableHead>
                  <TableHead className="text-right min-w-[100px]">Units Sold</TableHead>
                  <TableHead className="text-right min-w-[120px]">Gross Sales</TableHead>
                  <TableHead className="text-right min-w-[140px]">Previous Royalty Income</TableHead>
                  <TableHead className="text-right min-w-[140px]">Total Royalty Amount</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredReports.length > 0 ? (
                  filteredReports.map((bookReport) => (
                    <TableRow key={bookReport.id}>
                      <TableCell className="font-medium">
                        <div className="truncate max-w-[180px]" title={bookReport.books?.title || 'Unknown Book'}>
                          {bookReport.books?.title || 'Unknown Book'}
                        </div>
                      </TableCell>
                      <TableCell>
                        {(bookReport.bookFormat || 'paperback').charAt(0).toUpperCase() + (bookReport.bookFormat || 'paperback').slice(1)}
                      </TableCell>
                      <TableCell>
                        {normalizeCountryCode(bookReport.country || 'US')}
                      </TableCell>
                      <TableCell className="font-semibold">
                        Q{bookReport.quarter} {bookReport.year}
                      </TableCell>
                      <TableCell className="text-right">{bookReport.unitsSold || bookReport.salesCount}</TableCell>
                      <TableCell className="text-right">{formatCurrency(bookReport.grossSales || bookReport.amount)}</TableCell>
                      <TableCell className="text-right">{formatCurrency(calculatePreviousRoyaltyIncome(bookReport))}</TableCell>
                      <TableCell className="text-right">{formatCurrency(bookReport.royaltyAmount || bookReport.amount)}</TableCell>
                    </TableRow>
                  ))
                ) : fallbackReport ? (
                  // Fallback for single book report (legacy data)
                  <TableRow>
                    <TableCell className="font-medium">
                      <div className="truncate max-w-[180px]" title={fallbackReport.books?.title || 'Unknown Book'}>
                        {fallbackReport.books?.title || 'Unknown Book'}
                      </div>
                    </TableCell>
                    <TableCell>
                      {(fallbackReport.bookFormat || 'paperback').charAt(0).toUpperCase() + (fallbackReport.bookFormat || 'paperback').slice(1)}
                    </TableCell>
                    <TableCell>
                      {normalizeCountryCode(fallbackReport.country || 'US')}
                    </TableCell>
                    <TableCell className="font-semibold">
                      Q{fallbackReport.quarter} {fallbackReport.year}
                    </TableCell>
                    <TableCell className="text-right">{fallbackReport.unitsSold || fallbackReport.salesCount}</TableCell>
                    <TableCell className="text-right">{formatCurrency(fallbackReport.grossSales || fallbackReport.amount)}</TableCell>
                    <TableCell className="text-right">{formatCurrency(calculatePreviousRoyaltyIncome(fallbackReport))}</TableCell>
                    <TableCell className="text-right">{formatCurrency(fallbackReport.royaltyAmount || fallbackReport.amount)}</TableCell>
                  </TableRow>
                ) : (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center text-muted-foreground">
                      No sales data available for this quarter
                    </TableCell>
                  </TableRow>
                )}
                
                <TableRow className="font-medium bg-muted/30">
                  <TableCell>Total</TableCell>
                  <TableCell></TableCell>
                  <TableCell></TableCell>
                  <TableCell></TableCell>
                  <TableCell className="text-right">{totalSales}</TableCell>
                  <TableCell className="text-right">{formatCurrency(totalGrossSales)}</TableCell>
                  <TableCell className="text-right">{formatCurrency(totalPreviousRoyaltyIncome)}</TableCell>
                  <TableCell className="text-right">{formatCurrency(totalRoyaltyAmount)}</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </div>
        </ScrollArea>
      </CardContent>
    </Card>
  );
}
